import React, { SyntheticEvent, useCallback } from 'react';
import { capitalize } from 'lodash';

import { Icon } from '@zapier/common-components';
import { useLink } from '@zapier/common-hooks';

import ClickableTable from 'app/developer-v3/components/ClickableTable';
import ActionsDropdownMenu from 'app/developer-v3/components/ActionsDropdownMenu';
import {
  getAuthUrlForFieldKey,
  isLastDescendantNodeClicked,
} from 'app/developer-v3/utils';

import type { AuthFieldSchema } from 'app/developer-v3/platformSchema/field';
import type { VersionParams } from 'app/developer-v3/types/version';

type Props = {
  deleteAuthenticationField: (key: string) => void;
  fields: Array<AuthFieldSchema>;
  isReadOnly?: boolean;
  params: VersionParams;
};

const AuthenticationFields = (props: Props) => {
  const { deleteAuthenticationField, fields, isReadOnly, params } = props;

  const { routeTo } = useLink();

  const getFieldType = useCallback(field => {
    let fieldType = field.type || 'string';

    if (field.computed) {
      fieldType = 'computed';
    } else if (field.dynamic) {
      fieldType = 'dynamic dropdown';
    } else if (field.choices) {
      fieldType = 'static dropdown';
    }

    return capitalize(fieldType);
  }, []);

  const getMenuItems = useCallback(
    (key: string) => [
      {
        label: 'Edit',
        onChoose: () =>
          routeTo(getAuthUrlForFieldKey(params.id, params.version, key)),
        value: 'edit',
      },
      {
        isConfirmationRequired: true,
        label: 'Delete',
        modalTextContent: `Are you sure you want to delete the auth field of key: ${key}?`,
        modalTitle: 'Delete auth field',
        onChoose: () => deleteAuthenticationField(key),
        value: 'delete',
      },
    ],
    [deleteAuthenticationField, params, routeTo]
  );

  const onClickRow = useCallback(
    (event: SyntheticEvent<HTMLElement>, index: number) => {
      if (!isLastDescendantNodeClicked(event)) {
        routeTo(
          getAuthUrlForFieldKey(
            params.id,
            params.version,
            String(fields[index].key)
          )
        );
      }
    },
    [fields, params, routeTo]
  );

  const getCellModifiers = useCallback(
    (index: number) => ({
      center: [3, 4].includes(index),
      darken: index < 3,
    }),
    []
  );

  return (
    <section>
      <ClickableTable
        body={fields.map(field => [
          { value: '', element: field.label || capitalize(field.key) },
          { value: '', element: field.key },
          { value: '', element: getFieldType(field) },
          {
            value: '',
            element: (
              <Icon name={field.required ? 'check-alt' : 'restricted'} />
            ),
          },
          {
            value: '',
            element: !isReadOnly && (
              <ActionsDropdownMenu items={getMenuItems(field.key)} />
            ),
          },
        ])}
        getCellModifiers={getCellModifiers}
        header={[
          { title: 'Label', hasSort: false },
          { title: 'Key', hasSort: false },
          { title: 'Type', hasSort: false },
          { title: 'Required', hasSort: false },
          { title: '', hasSort: false },
        ]}
        onClickRow={onClickRow}
        rowAriaLabel="Click to edit this trigger"
      />
    </section>
  );
};

export default AuthenticationFields;
